import { useState } from 'react';
import styled from 'styled-components';
import { useCommentGeoSchemeMutation } from '@shared/api';
import { eventTitle, fmtDateTime, type GeoEvent, type GeoScheme } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { useAction } from './hooks';
import { ErrorText, Muted, Row, Stack, TextArea } from './ui';

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Item = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};

  /* Вертикальная нить журнала между маркерами. */
  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 16px;
    bottom: 0;
    width: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
  &:last-child::before {
    display: none;
  }
`;

const Marker = styled.span<{ $tone: 'danger' | 'success' | 'neutral' | 'comment' }>`
  width: 13px;
  height: 13px;
  margin-top: 3px;
  border-radius: ${({ $tone, theme }) => ($tone === 'comment' ? theme.radius.xs : '50%')};
  border: 2px solid
    ${({ theme, $tone }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'success'
          ? theme.colors.status.success
          : $tone === 'comment'
            ? theme.colors.status.info
            : theme.colors.borderStrong};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  font-size: 13.5px;
`;

const Title = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Text = styled.div<{ $danger: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 13.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-wrap: pretty;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.status.danger : theme.colors.text.primary)};
`;

function toneOf(e: GeoEvent): 'danger' | 'success' | 'neutral' | 'comment' {
  if (e.to_status === 'rejected') return 'danger';
  if (e.to_status === 'approved' || e.to_status === 'accepted') return 'success';
  if (e.kind.toLowerCase().includes('comment')) return 'comment';
  return 'neutral';
}

/**
 * Журнал ИС: переходы статусов, причины отклонения, комментарии и правки —
 * постоянная обратная связь ОСНОВА.Инженер ↔ ОСНОВА.Геодезист (ТЗ §6).
 * Свежие события сверху.
 */
export function SchemeJournal({ scheme, canComment }: { scheme: GeoScheme; canComment: boolean }) {
  const [comment] = useCommentGeoSchemeMutation();
  const { run, busy, error } = useAction();
  const [text, setText] = useState('');
  const events = scheme.events
    .slice()
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));

  async function send() {
    const value = text.trim();
    if (!value) return;
    const ok = await run(
      () => comment({ id: scheme.id, text: value }).unwrap(),
      'Комментарий не отправлен.',
    );
    if (ok) setText('');
  }

  return (
    <Stack $gap={4}>
      {canComment && (
        <Stack $gap={2}>
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Комментарий для геодезиста и инженера ПТО"
            aria-label="Комментарий в журнал схемы"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Row $gap={3}>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={() => void send()}
              disabled={busy || text.trim() === ''}
            >
              {busy ? 'Отправка…' : 'Добавить в журнал'}
            </Button>
            <Muted>Ctrl+Enter — отправить</Muted>
          </Row>
          {error && <ErrorText role="alert">{error}</ErrorText>}
        </Stack>
      )}
      {events.length === 0 ? (
        <Muted>Событий пока нет.</Muted>
      ) : (
        <List aria-label="Журнал схемы">
          {events.map((e) => {
            const tone = toneOf(e);
            return (
              <Item key={e.id}>
                <Marker $tone={tone} aria-hidden />
                <div>
                  <Head>
                    <Title>{eventTitle(e)}</Title>
                    <Meta>
                      {e.actor_name ? `${e.actor_name} · ` : ''}
                      {fmtDateTime(e.created_at)}
                    </Meta>
                  </Head>
                  {e.text && <Text $danger={tone === 'danger'}>{e.text}</Text>}
                </div>
              </Item>
            );
          })}
        </List>
      )}
    </Stack>
  );
}
